import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { PeriodicTableIcon } from '../../components/PeriodicTableIcon';
import { BondGlyph } from '../../components/BondGlyph';
import { RingIcon } from '../../components/RingIcon';
import { ShapeGlyph } from '../../components/AnnotationGlyph';
import { CalloutGlyph } from './CalloutsGroup';
import { ReactionArrowGlyph } from '../../components/ReactionArrowGlyph';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

export type RailGroupId =
  'bonds' | 'cycles' | 'groups' | 'markush' | 'shapes' | 'reactionArrows' | 'callouts' | 'templates';

/** DOM id on each rail group's container, so the collapsed strip can scroll to it on expand. */
export function railGroupDomId(id: RailGroupId): string {
  return `rail-group-${id}`;
}

interface RailIconStripProps {
  readonly tool: EditorTool;
  readonly onOpenPeriodicTable: () => void;
  readonly onExpand: () => void;
  readonly onExpandToGroup: (id: RailGroupId) => void;
}

// #413 — Reaction arrows sits above Shapes, matching the expanded rail order.
const GROUPS: readonly { id: RailGroupId; icon: ReactNode; labelKey: string }[] = [
  { id: 'bonds', icon: <BondGlyph variant="single" />, labelKey: 'workspace.rail.groups.bonds' },
  { id: 'cycles', icon: <RingIcon size={6} aromatic />, labelKey: 'workspace.rail.groups.cycles' },
  // #736 -- functional groups: a group's own text is the glyph.
  {
    id: 'groups',
    icon: (
      <span className="r-group-glyph" aria-hidden="true">
        CF₃
      </span>
    ),
    labelKey: 'workspace.rail.groups.functionalGroups'
  },
  // #674 -- Markush tools: the R label is the glyph, as on the expanded rail.
  {
    id: 'markush',
    icon: (
      <span className="r-group-glyph" aria-hidden="true">
        R
      </span>
    ),
    labelKey: 'workspace.rail.groups.markush'
  },
  {
    id: 'reactionArrows',
    icon: <ReactionArrowGlyph kind="forward" />,
    labelKey: 'workspace.rail.groups.reactionArrows'
  },
  { id: 'shapes', icon: <ShapeGlyph variant="rect" />, labelKey: 'workspace.rail.groups.shapes' },
  { id: 'callouts', icon: <CalloutGlyph />, labelKey: 'workspace.rail.shapes.callout.groupLabel' },
  { id: 'templates', icon: <Icon name="folder" />, labelKey: 'workspace.rail.groups.templates' }
];

// #400 — the collapsed left rail: an icon-only strip. Top pointer tools stay live (draw /
// select / periodic table); each group is one icon that expands the rail and scrolls to that
// group. Every control carries `aria-label` + `title` (icons-over-words).
export function RailIconStrip({ tool, onOpenPeriodicTable, onExpand, onExpandToGroup }: RailIconStripProps) {
  const { t } = useTranslation();
  const armedZ = tool.startsWith('element-') ? Number(tool.slice('element-'.length)) : null;
  const elementsLabel =
    armedZ !== null
      ? t('workspace.rail.periodicTable.armed', { symbol: elementSymbol(armedZ) })
      : t('workspace.rail.periodicTable.open');

  return (
    <div className="workspace-rail-strip">
      <button
        type="button"
        className="icon-button workspace-rail-toggle"
        aria-label={t('workspace.rail.expand')}
        title={t('workspace.rail.expand')}
        onClick={onExpand}
      >
        <Icon name="chevronRight" />
      </button>

      <div className="workspace-rail-strip-tools" role="group" aria-label={t('workspace.rail.label')}>
        <button
          type="button"
          className={`icon-button ${armedZ !== null ? 'active' : ''}`}
          aria-label={elementsLabel}
          title={elementsLabel}
          aria-pressed={armedZ !== null}
          onClick={onOpenPeriodicTable}
        >
          <PeriodicTableIcon />
        </button>
      </div>

      <div className="workspace-rail-strip-groups" role="group" aria-label={t('workspace.rail.label')}>
        {GROUPS.map(({ id, icon, labelKey }) => (
          <button
            key={id}
            type="button"
            className="icon-button"
            aria-label={t(labelKey)}
            title={t(labelKey)}
            onClick={() => onExpandToGroup(id)}
          >
            {icon}
          </button>
        ))}
      </div>
    </div>
  );
}
